import { useState, useEffect, useCallback } from "react";
import { Box, Typography, TextField, CircularProgress, ClickAwayListener } from "@material-ui/core";
import dayjs from "dayjs";
import { renderStatus } from "utils/campaignQuote";

import EditIcon2 from "static/media/edit-icon-2";
import { SummaryContentText } from "../SummaryContentText";
import { SummaryTitleText } from "../SummaryTitleText";
import { TitleText } from "../TitleText";
import { CampaignInfoFormData } from "campaign-builder/domain/schemas/campaignInformation";

import "./CampaignDetailsSection.css";
import { DescriptionText } from "../DescriptionText";
import { FORMATS } from "pages/flyer/builder/constants";
import { LaunchDatePicker } from "./LaunchDatePicker";
import { addDays, addBusinessDays } from "components/date-picker/utils";
import { LightTooltip } from "components/light-tooltip";
import { Icon } from "components/icons";
import { DM_MIN_SELECTABLE_DATE } from "../../../../../constants";

export const CampaignDetailsSection = ({
  campaign,
  onEditFlyerFormat,
  isReadOnly,
  campaignStatus,
  // EDDM-specific props for date picker
  isEddm = false,
  onStartDateChange,
  launchDateError,
  timezone = "America/New_York",
  isSubmitted = false,
  // Campaign name editing
  onCampaignNameChange,
}: {
  campaign: Partial<CampaignInfoFormData>;
  onEditFlyerFormat?: () => void;
  isReadOnly?: boolean;
  campaignStatus: {
    internalStatus?: string;
    reviewMeetingDate?: number;
  };
  // EDDM-specific props
  isEddm?: boolean;
  onStartDateChange?: (date: Date | null) => void;
  launchDateError?: string | null;
  timezone?: string;
  isSubmitted?: boolean;
  // Campaign name editing
  onCampaignNameChange?: (name: string) => Promise<{ success: boolean; error?: string }>;
}) => {
  const format = FORMATS[campaign.format as keyof typeof FORMATS];

  // Campaign name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(campaign.campaignName || "");
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);

  // Update local state when campaign name changes from props
  useEffect(() => {
    if (campaign.campaignName && !isEditingName) {
      setEditedName(campaign.campaignName);
    }
  }, [campaign.campaignName, isEditingName]);

  // Save campaign name
  const saveCampaignName = useCallback(async () => {
    if (!onCampaignNameChange) return;

    // If empty, reset to original
    if (!editedName.trim()) {
      setEditedName(campaign.campaignName || "");
      setNameError(null);
      setIsEditingName(false);
      return;
    }

    // If unchanged, just close
    if (editedName.trim() === campaign.campaignName) {
      setNameError(null);
      setIsEditingName(false);
      return;
    }

    setIsSavingName(true);

    try {
      const result = await onCampaignNameChange(editedName);
      if (result.success) {
        setNameError(null);
        setIsEditingName(false);
      } else {
        setNameError(result.error || "Failed to save campaign name");
      }
    } catch (error) {
      setNameError("Failed to save campaign name");
    } finally {
      setIsSavingName(false);
    }
  }, [editedName, campaign.campaignName, onCampaignNameChange]);

  // Handle click away from name input
  const handleNameClickAway = useCallback(() => {
    if (isEditingName && !isSavingName) {
      saveCampaignName();
    }
  }, [isEditingName, isSavingName, saveCampaignName]);

  // Handle name input key press
  const handleNameKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        saveCampaignName();
      } else if (e.key === "Escape") {
        setEditedName(campaign.campaignName || "");
        setNameError(null);
        setIsEditingName(false);
      }
    },
    [saveCampaignName, campaign.campaignName]
  );

  // Check if name editing is enabled
  const canEditName = !isReadOnly && !isSubmitted && onCampaignNameChange;

  // Date picker min/max for EDDM
  const minStartDate = isEddm ? addBusinessDays(new Date(), DM_MIN_SELECTABLE_DATE, timezone) : undefined;
  const maxStartDate = isEddm ? addDays(new Date(), 360, timezone) : undefined;
  // A saved date can fall behind the lead time after it was picked; flag it like the review page does (PAD-33530).
  const isSavedDateTooEarly =
    !!minStartDate &&
    !isReadOnly &&
    !isSubmitted &&
    campaign.campaignStartDate instanceof Date &&
    dayjs(campaign.campaignStartDate).isBefore(minStartDate, "day");

  return (
    <div className="campaignDetailsSectionContainer" data-cypress-id={"campaignDetailsSectionContainer"}>
      <Box className="campaignSummarySectionTitle" data-cypress-id={"campaignSummarySectionTitle"}>
        <TitleText>Summary</TitleText>
        <div>{renderStatus(campaignStatus.internalStatus, campaignStatus.reviewMeetingDate)}</div>
      </Box>
      {isEditingName ? (
        <ClickAwayListener onClickAway={handleNameClickAway}>
          <Box className="campaignSummarySectionColumn">
            <SummaryTitleText data-cypress-id={"campaignSummaryCampaignNameTitle"}>Campaign name</SummaryTitleText>
            <Box className="campaignNameEditContainer">
              <TextField
                autoFocus
                fullWidth
                size="small"
                variant="outlined"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleNameKeyPress}
                disabled={isSavingName}
                error={!!nameError}
                placeholder="Enter campaign name"
                inputProps={{ maxLength: 46 }}
                InputProps={{
                  style: { fontSize: 14 },
                  endAdornment: isSavingName ? <CircularProgress size={16} color="secondary" /> : null,
                }}
              />
              {nameError && (
                <span className="campaignNameError" data-cypress-id={"campaignNameError"}>
                  {nameError}
                </span>
              )}
            </Box>
          </Box>
        </ClickAwayListener>
      ) : (
        <Box className="campaignSummarySection">
          <SummaryTitleText data-cypress-id={"campaignSummaryCampaignNameTitle"}>Campaign name</SummaryTitleText>
          <Box className="campaignNameDisplayContainer">
            <SummaryContentText data-cypress-id={"campaignSummaryCampaignName"}>
              {campaign.campaignName}
            </SummaryContentText>
            {canEditName && (
              <Box
                className="campaignNameEditIcon"
                onClick={() => setIsEditingName(true)}
                data-cypress-id={"editCampaignNameButton"}
              >
                <EditIcon2 />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* EDDM: Show date picker; Others: Show static date */}
      {isEddm && onStartDateChange ? (
        <Box className="campaignSummarySectionColumn">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <SummaryTitleText>Launch date</SummaryTitleText>
              <LightTooltip
                placement="top-start"
                title={
                  <Typography style={{ fontSize: "14px" }}>
                    {`Launch date must be set not earlier than ${DM_MIN_SELECTABLE_DATE} business days from today`}
                  </Typography>
                }
              >
                <Box ml="8px" display="flex" alignItems="center">
                  <Icon type="InfoEmpty" size="xs" />
                </Box>
              </LightTooltip>
            </Box>
            {!campaign.campaignStartDate && !isSubmitted && (
              <span style={{ color: "#ee4360", fontSize: "12px", fontWeight: 500 }}>Required</span>
            )}
          </Box>
          <Box className="eddmDatePickerWrapper">
            <LaunchDatePicker
              value={campaign.campaignStartDate instanceof Date ? campaign.campaignStartDate : null}
              onChange={onStartDateChange}
              error={launchDateError ?? (isSavedDateTooEarly ? "Select a different date" : null)}
              minDate={minStartDate}
              maxDate={maxStartDate}
              disabled={isReadOnly || isSubmitted}
            />
          </Box>
        </Box>
      ) : campaign.campaignStartDate && dayjs(campaign.campaignStartDate).isValid() ? (
        <Box className="campaignSummarySection">
          <SummaryTitleText data-cypress-id={"campaignSummaryLaunchDateTitle"}>Launch date</SummaryTitleText>
          <SummaryContentText data-cypress-id={"campaignSummaryLaunchDate"}>
            {dayjs(campaign.campaignStartDate).format("MMM D, YYYY")}
          </SummaryContentText>
        </Box>
      ) : null}

      <Box className="campaignSummarySection">
        <SummaryTitleText data-cypress-id={"campaignSummaryFormatTitle"}>Format</SummaryTitleText>
        <Box className="campaignSummaryFormatContainer" data-cypress-id="campaignSummaryFormatContainer">
          <SummaryContentText data-cypress-id={"campaignSummaryFormatTopText"}>{`${format?.name}`}</SummaryContentText>
          <DescriptionText
            className="campaignSummaryFormatBottomText"
            data-cypress-id={"campaignSummaryFormatBottomText"}
          >
            {format?.price}
          </DescriptionText>
        </Box>
      </Box>
    </div>
  );
};
